import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Radio, Coffee, Clock } from "lucide-react";

const fmt = (s) => {
  const sec = Math.max(0, s || 0);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

export default function OnTheClockWidget() {
  const [data, setData] = useState({ count: 0, workers: [] });
  const [, setTick] = useState(0);
  const poll = useRef(null);
  const load = async () => {
    try { const { data } = await api.get("/timeclock/active"); setData(data); } catch { /* transient */ }
  };
  useEffect(() => {
    load();
    poll.current = setInterval(load, 20000);
    const live = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { clearInterval(poll.current); clearInterval(live); };
  }, []);

  const liveWork = (w) => {
    if (!w.since) return w.work_seconds || 0;
    const now = Date.now();
    const start = new Date(w.since).getTime();
    // work_seconds already excludes finished breaks; only add live delta since server as_of when working
    return w.state === "on_break" ? (w.work_seconds || 0) : Math.floor((now - start) / 1000) - (w.break_seconds || 0);
  };

  return (
    <div data-testid="on-the-clock-widget" className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <span className="relative flex h-2.5 w-2.5">
          <span className={`absolute inline-flex h-full w-full rounded-full ${data.count ? "bg-emerald-400 animate-ping" : "bg-slate-300"} opacity-75`} />
          <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${data.count ? "bg-emerald-500" : "bg-slate-400"}`} />
        </span>
        <h2 className="flex items-center gap-1.5 text-sm font-700 text-[#1A395E]"><Radio className="h-4 w-4 text-teal" /> On the Clock Now</h2>
        <span data-testid="otc-count" className="ml-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700">{data.count}</span>
      </div>
      {data.count === 0 ? (
        <p data-testid="otc-empty" className="text-[13px] text-slate-400">No one is clocked in right now.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {data.workers.map((w) => (
            <div key={w.user_id} data-testid={`otc-worker-${w.user_id}`}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${w.state === "on_break" ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-[11px] font-bold text-[#1A395E] shadow-sm">
                {(w.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
              </div>
              <div className="leading-tight">
                <div className="text-[13px] font-semibold text-slate-800">{w.name}</div>
                <div className="flex items-center gap-1.5 text-[11px]">
                  {w.state === "on_break" ? (
                    <span className="inline-flex items-center gap-1 font-semibold text-amber-700"><Coffee className="h-3 w-3" /> On break</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 font-semibold text-emerald-700"><Clock className="h-3 w-3" /> {fmt(liveWork(w))}</span>
                  )}
                  {w.job_role && <span className="text-slate-400">· {w.job_role}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
